import React from 'react';
import { Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DatePickerField from '../../form/DatePickerField';
import { isRequiredField } from '../../form/RequiredAsterisk';
import SelectField from '../../form/SelectField';
import CheckboxesField from '../../form/CheckboxesField';
import RadiosField from '../../form/RadiosField';
import LabelField from '../../form/LabelField';
import ScrollToFirstError from '../../route/ScrollToFirstError';

const ProfileForm = ({
    schema,
    onSubmit,
    initialValues,
    dataTypes,
    isLoading,
    isSessionExpired,
    clientLoading,
    findClient,
}) => {
    const { questionPhrasing } = dataTypes;
    const enabledFields = dataTypes.enabledFields || [];
    const { t } = useTranslation();
    const location = useLocation();
    const isPartialProfile =
        (location && location.state !== undefined && location.state?.partialProfile) || isSessionExpired;
    const isEdit = location && location.state !== undefined && location.state?.edit && location.state?.edit === true;

    return (
        <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {({ handleSubmit, handleChange, values, errors }) => (
                <Col md={6} sm={12} className="mx-auto">
                    <Form noValidate onSubmit={handleSubmit} className="card shadow-sm rounded p-4">
                        <h4 className="text-center">{t('intake.heading')}</h4>
                        <p className="text-center text-muted mb-4">
                            {t('intake.step1Of3')}
                        </p>

                        {dataTypes.householdMessage && (
                            <p className="text-center" dangerouslySetInnerHTML={{ __html: dataTypes.householdMessage }}></p>
                        )}

                        {clientLoading && (
                            <div className="text-center">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        )}

                        {!clientLoading && (
                            <>
                                {isSessionExpired && (
                                    <Alert variant="danger">
                                        {t('session.sessionExpiredNotice', { dob_label: questionPhrasing.dob })}
                                    </Alert>
                                )}

                                <Form.Group controlId="clientFirstName">
                                    <LabelField
                                        label={questionPhrasing.firstName || t('personal.firstName')}
                                        isRequired={isRequiredField(schema, 'firstName')}
                                    />
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        placeholder="First Name"
                                        value={values.firstName || ''}
                                        onChange={handleChange}
                                        isInvalid={!!errors.firstName}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="clientLastName">
                                    <LabelField
                                        label={questionPhrasing.lastName || t('personal.lastName')}
                                        isRequired={isRequiredField(schema, 'lastName')}
                                    />
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        placeholder="Last Name"
                                        value={values.lastName || ''}
                                        onChange={handleChange}
                                        isInvalid={!!errors.lastName}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                                </Form.Group>

                                {enabledFields.includes('dateOfBirth') && (isPartialProfile || isEdit) && (
                                    <Form.Group controlId="dob">
                                        <LabelField
                                            label={questionPhrasing.dob || t('personal.dob')}
                                            isRequired={isRequiredField(schema, 'dob')}
                                        />
                                        <DatePickerField
                                            className={!!errors.dob ? 'form-control is-invalid' : 'form-control'}
                                            id="dob"
                                            name="dob"
                                            popperClassName="d-none"
                                            placeholderText={dataTypes.dateFormat.toUpperCase()}
                                            dateFormat={dataTypes.dateFormat}
                                            maxDate={new Date()}
                                            showYearDropdown
                                            dateFormatCalendar="MMMM"
                                            yearDropdownItemNumber={15}
                                            scrollableYearDropdown
                                            autoComplete="off"
                                            onChange={(date) => findClient(date)}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            {t('intake.dobHelper')}
                                        </Form.Text>
                                    </Form.Group>
                                )}

                                {enabledFields.includes('age') && (isPartialProfile || isEdit) && (
                                    <Form.Group controlId="age">
                                        <LabelField
                                            label={questionPhrasing.age || t('personal.age')}
                                            isRequired={isRequiredField(schema, 'age')}
                                        />
                                        <Form.Control
                                            type="text"
                                            className={!!errors.age ? 'form-control is-invalid' : 'form-control'}
                                            id="age"
                                            name="age"
                                            placeholder="Age"
                                            value={values.age || ''}
                                            onChange={handleChange}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.age}</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                {enabledFields.includes('gender') && (
                                    <Form.Group controlId="gender">
                                        <LabelField
                                            label={questionPhrasing.gender || t('intake.gender')}
                                            isRequired={isRequiredField(schema, 'gender')}
                                        />
                                        <SelectField
                                            as="select"
                                            name="gender"
                                            value={values.gender}
                                            isInvalid={!!errors.gender}
                                            options={dataTypes.genders}
                                            includeEmpty={true}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                {enabledFields.includes('maritalStatus') && (
                                    <Form.Group controlId="maritalStatus">
                                        <LabelField
                                            label={questionPhrasing.maritalStatus || t('intake.maritalStatus')}
                                            isRequired={isRequiredField(schema, 'maritalStatus')}
                                        />
                                        <SelectField
                                            as="select"
                                            name="maritalStatus"
                                            value={values.maritalStatus}
                                            isInvalid={!!errors.maritalStatus}
                                            options={dataTypes.maritalStatuses}
                                            includeEmpty={true}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.maritalStatus}</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                {enabledFields.includes('isHispanicLatino') && (
                                    <RadiosField
                                        schema={schema}
                                        types={dataTypes.isHispanicLatino}
                                        name="isHispanicLatino"
                                        value={values.isHispanicLatino}
                                        label={questionPhrasing.isHispanicLatino || t('intake.isHispanicLatino')}
                                    />
                                )}

                                {enabledFields.includes('ethnicity') && (
                                    <CheckboxesField
                                        schema={schema}
                                        types={dataTypes.ethnicities}
                                        name="ethnicityType"
                                        value={values.ethnicityType}
                                        label={
                                            questionPhrasing.ethnicities ||
                                            (enabledFields.includes('isHispanicLatino')
                                                ? t('intake.ethnicities_alternate')
                                                : t('intake.ethnicities'))
                                        }
                                    />
                                )}

                                {enabledFields.includes('selfIdentificationType') && (
                                    <CheckboxesField
                                        schema={schema}
                                        types={dataTypes.selfIdentificationTypes}
                                        name="selfIdentificationType"
                                        value={values.selfIdentificationType}
                                        label={questionPhrasing.selfIdentifiesAs || t('intake.selfIdentifiesAs')}
                                    />
                                )}

                                {enabledFields.includes('educationType') && (
                                    <Form.Group controlId="educationType">
                                        <LabelField
                                            label={questionPhrasing.education || t('intake.education')}
                                            isRequired={isRequiredField(schema, 'educationType')}
                                        />
                                        <SelectField
                                            as="select"
                                            name="educationType"
                                            value={values.educationType}
                                            isInvalid={!!errors.educationType}
                                            options={dataTypes.educationTypes}
                                            includeEmpty={true}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.educationType}</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                {enabledFields.includes('employmentType') && (
                                    <Form.Group controlId="employmentType">
                                        <LabelField
                                            label={questionPhrasing.employment || t('intake.employment')}
                                            isRequired={isRequiredField(schema, 'employmentType')}
                                        />
                                        <SelectField
                                            as="select"
                                            name="employmentType"
                                            value={values.employmentType}
                                            isInvalid={!!errors.employmentType}
                                            options={dataTypes.employmentTypes}
                                            includeEmpty={true}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.employmentType}</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                <Form.Group className="text-center mt-4 mb-0">
                                    <Button variant="primary" type="submit" disabled={isLoading} id="nextButton" className="w-100">
                                        {isLoading ? t('core.action.loading') : t('core.action.next')}
                                    </Button>
                                </Form.Group>
                                <ScrollToFirstError />
                            </>
                        )}
                    </Form>
                    {isEdit && (
                        <p className="text-center mt-3 mb-0">
                            <Link to="/dashboard">{t('dashboard.return')}</Link>
                        </p>
                    )}
                </Col>
            )}
        </Formik>
    );
};

export default ProfileForm;
